<template>
  <div class="group-row" :class="{ 'is-headerless': isHeaderless }">
    <div v-if="!isHeaderless" class="group-header-wrap">
      <button
        class="group-header"
        :aria-expanded="expanded"
        @click="$emit('toggle')"
      >
        <i class="material-icons chevron" aria-hidden="true">{{ expanded ? 'expand_more' : 'chevron_right' }}</i>
        <span class="group-icon-wrapper" :style="{ backgroundColor: bgTint, color: $theme.primaryColor }">
          <i class="material-icons">{{ kindIcon }}</i>
        </span>
        <span class="group-info">
          <span class="group-label">{{ group.name }}</span>
          <span class="group-meta">
            {{ group.learningObjects.length }} {{ group.learningObjects.length === 1 ? 'aula' : 'aulas' }}
            <template v-if="!expanded && sizeLabel"> · {{ sizeLabel }}</template>
          </span>
        </span>
      </button>
      <button
        class="group-actions-btn"
        aria-label="Mais ações do módulo"
        @click.stop="$emit('open-group-actions', { enrollId, name: group.name, loIds: groupLoIds })"
      >⋮</button>
    </div>
    <div v-show="expanded || isHeaderless" class="learning-objects">
      <DownloadedLearningObjectRow
        v-for="lo in group.learningObjects"
        :key="lo.id"
        :learning-object="lo"
        :enroll-id="enrollId"
        :position-seconds="positionOf(lo.id)"
        :has-access="hasAccess"
        @play="$emit('play', $event)"
        @open-actions="$emit('open-actions', $event)"
      />
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import DownloadedLearningObjectRow from './DownloadedLearningObjectRow.vue';
import { formatBytes } from '@/services/download/downloadHelpers';

export default {
  name: 'DownloadedGroupRow',
  components: { DownloadedLearningObjectRow },
  props: {
    group: { type: Object, required: true },
    enrollId: { type: [String, Number], required: true },
    expanded: { type: Boolean, default: false },
    // Repassado pelo CourseCard. false → LO rows ficam desaturadas e
    // não disparam play. Group ainda tem ações de remoção via ActionsSheet.
    hasAccess: { type: Boolean, default: true }
  },
  computed: {
    ...mapGetters('download', ['getLocalPosition']),
    isHeaderless() {
      return !this.group.name;
    },
    kindIcon() {
      return this.group.kind === 'class' ? 'play_circle_outline' : 'class_';
    },
    sizeLabel() {
      return this.group.totalSize ? formatBytes(this.group.totalSize) : '';
    },
    groupLoIds() {
      return this.group.learningObjects.map(lo => lo.id);
    },
    bgTint() {
      const c = this.$theme.primaryColor || '#ffffff';
      if (!c.startsWith('#')) return c;
      const clean = c.replace('#', '');
      const full = clean.length === 3 ? clean.split('').map(x => x + x).join('') : clean;
      const r = parseInt(full.slice(0, 2), 16);
      const g = parseInt(full.slice(2, 4), 16);
      const b = parseInt(full.slice(4, 6), 16);
      return `rgba(${r}, ${g}, ${b}, 0.12)`;
    }
  },
  methods: {
    positionOf(loId) {
      return this.getLocalPosition(this.enrollId, loId);
    }
  }
};
</script>

<style scoped lang="scss">
.group-row {
  border-top: 1px solid var(--md-border-strong);
}

.group-header-wrap {
  display: flex;
  align-items: stretch;
  background: var(--md-surface-elev);
}

.group-header {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
  padding: 14px 16px;
  background: transparent;
  border: none;
  cursor: pointer;
  text-align: left;
}

.group-icon-wrapper {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 6px;
  flex-shrink: 0;

  .material-icons { font-size: 16px; }
}

.group-info {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-width: 0;
  gap: 2px;
}

.group-label {
  font-size: 13px;
  font-weight: 600;
  color: var(--md-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.group-meta {
  font-size: 11px;
  color: var(--md-text-muted);
}

.chevron {
  color: var(--md-text-dim);
  font-size: 22px;
  flex-shrink: 0;
}

.group-actions-btn {
  flex-shrink: 0;
  width: 40px;
  background: transparent;
  border: none;
  color: var(--md-text-dim);
  font-size: 22px;
  line-height: 1;
  cursor: pointer;
  padding: 0 4px;

  &:active { color: var(--md-text); }
}

.learning-objects {
  padding: 8px 0 12px 16px;
}
</style>
